import React, { useCallback, useEffect, useState } from 'react'

import {
  DeleteOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
  UploadOutlined,
  InboxOutlined,
  StopOutlined
} from '@ant-design/icons'
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Spin,
  Alert,
  Table,
  Space,
  Progress,
  Modal,
  Tooltip,
  message,
  Upload
} from 'antd'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import ListPageHeader from '@shared/ListPageHeader'
import InventoryConflictSection from '@shared/inventory/InventoryConflictSection'

import { useBackgroundJob } from '@/components/shared/BackgroundJobManager'
import { stockTakingService } from '@services/operator/stockTakingService'
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler'
import ItemsSpreadsheet from './components/ItemsSpreadsheet'

const { Dragger } = Upload

export default function OperatorStockTakingShow() {
  const { id } = useParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [stockTaking, setStockTaking] = useState(null)
  const [error, setError] = useState(null)
  const [approving, setApproving] = useState(false)
  const [rejecting, setRejecting] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [conflicts, setConflicts] = useState([])
  const [previewLoading, setPreviewLoading] = useState(false)

  // 儲位列表狀態
  const [shelves, setShelves] = useState([])
  const [shelvesLoading, setShelvesLoading] = useState(false)
  const [shelvesPagination, setShelvesPagination] = useState({
    current: 1,
    pageSize: 20,
    hasMore: false
  })

  // 匯入 Modal 狀態
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [importFile, setImportFile] = useState(null)
  const [importing, setImporting] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [confirmingAll, setConfirmingAll] = useState(false)
  const [zeroingOut, setZeroingOut] = useState(false)

  // Spreadsheet 內未儲存修改的筆數（由 ItemsSpreadsheet 透過 onDirtyChange 提供）
  const [spreadsheetDirtyCount, setSpreadsheetDirtyCount] = useState(0)
  // 匯入完成後 bump，作 ItemsSpreadsheet 的 key 強制 remount 重抓資料；
  // 因為 spreadsheet items 是元件內部 state，光 reload 父層的 stock_taking metadata 不夠。
  const [spreadsheetReloadKey, setSpreadsheetReloadKey] = useState(0)

  const { addJob } = useBackgroundJob()

  useEffect(() => {
    loadStockTaking()
  }, [id])

  // 靜態盤點快照狀態輪詢
  useEffect(() => {
    if (!stockTaking) return
    if (stockTaking.mode !== 'static') return
    if (stockTaking.snapshot_ready) return
    if (stockTaking.snapshot_status === 'failed') return

    // 每 2 秒輪詢一次
    const interval = setInterval(() => {
      loadStockTaking()
    }, 2000)

    return () => clearInterval(interval)
  }, [stockTaking?.id, stockTaking?.snapshot_status, stockTaking?.snapshot_ready])

  const loadStockTaking = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await stockTakingService.get(id)

      if (response.success && response.data) {
        setStockTaking(response.data)
        // 載入儲位列表
        loadShelves(1)
      } else {
        setError(response.message || t('common.error.load_failed'))
      }
    } catch (err) {
      console.error('Load stock taking error:', err)
      setError(t('common.error.unknown_error'))
    } finally {
      setLoading(false)
    }
  }

  // 載入儲位列表
  const loadShelves = async (page = 1, pageSize = 20) => {
    try {
      setShelvesLoading(true)
      const response = await stockTakingService.getShelves(id, {
        page,
        page_size: pageSize
      })

      if (response.success) {
        setShelves(response.data || [])
        setShelvesPagination({
          current: page,
          pageSize: pageSize,
          hasMore: response.has_more || false
        })
      } else {
        message.error(response.message || t('common.error.load_failed'))
      }
    } catch (err) {
      console.error('Load shelves error:', err)
      message.error(t('common.error.unknown_error'))
    } finally {
      setShelvesLoading(false)
    }
  }

  // 處理儲位分頁變化
  const handleShelvesTableChange = (pagination) => {
    loadShelves(pagination.current, pagination.pageSize)
  }

  // 若 spreadsheet 內還有未儲存修改，擋下所有會改變 stock_taking 狀態的動作。
  const blockIfDirty = () => {
    if (spreadsheetDirtyCount > 0) {
      Modal.warning({
        title: t('shared.stock_takings.show.spreadsheet.leave_title'),
        content: t('shared.stock_takings.show.spreadsheet.leave_content', { count: spreadsheetDirtyCount })
      })
      return true
    }
    return false
  }

  // 匯出項目
  const handleExportItems = async () => {
    try {
      setExporting(true)
      const response = await stockTakingService.exportItems(id)
      if (response.success) {
        // 註冊到 BackgroundJobManager，未註冊時 worker broadcast 的 download_url 會被 reducer 丟棄
        addJob(response.data.job_id, 'shared.stock_takings.show.export_items')
        message.success(t('shared.stock_takings.show.export_items_job_name'))
      } else {
        message.error(response.message || t('common.error.export_failed'))
      }
    } catch (err) {
      console.error('Export items error:', err)
      message.error(t('common.error.unknown_error'))
    } finally {
      setExporting(false)
    }
  }

  // 匯入項目
  const handleImportItems = async () => {
    if (!importFile) {
      message.warning(t('common.import_modal.no_file'))
      return
    }

    try {
      setImporting(true)
      const response = await stockTakingService.importItems(id, importFile)
      if (response.success) {
        // API 只代表 worker 已排入 Sidekiq，counted_quantity 仍是匯入前狀態。
        // 真的更新完是 worker broadcast `completed` 之後 — 那一刻才能 reload。
        if (response.data?.job_id) {
          addJob(response.data.job_id, 'shared.stock_takings.show.import_items', {
            onComplete: () => {
              loadStockTaking()
              setSpreadsheetReloadKey((k) => k + 1)
            }
          })
        }
        message.success(t('shared.stock_takings.show.import_success'))
        setImportModalOpen(false)
        setImportFile(null)
      } else {
        message.error(response.message || t('shared.stock_takings.show.import_failed'))
      }
    } catch (err) {
      console.error('Import items error:', err)
      message.error(t('common.error.unknown_error'))
    } finally {
      setImporting(false)
    }
  }

  // 確認所有儲位
  const handleConfirmAllShelves = () => {
    if (blockIfDirty()) return
    Modal.confirm({
      title: t('shared.stock_takings.show.confirm_all_shelves_confirm_title'),
      icon: <ExclamationCircleOutlined />,
      content: t('shared.stock_takings.show.confirm_all_shelves_confirm_content'),
      okText: t('common.action.confirm'),
      cancelText: t('common.action.cancel'),
      onOk: async () => {
        setConfirmingAll(true)
        try {
          const response = await stockTakingService.confirmAllShelves(id)
          if (response.success) {
            message.success(t('shared.stock_takings.show.confirm_all_shelves_success'))
            loadStockTaking()
          } else {
            message.error(response.message || t('shared.stock_takings.show.confirm_all_shelves_failed'))
          }
        } catch (err) {
          console.error('Confirm all shelves error:', err)
          message.error(t('common.error.unknown_error'))
        } finally {
          setConfirmingAll(false)
        }
      }
    })
  }

  // 將未盤項目 (counted_quantity IS NULL) 批次標記為 0
  const handleZeroOutUncounted = () => {
    if (blockIfDirty()) return

    Modal.confirm({
      title: t('shared.stock_takings.show.zero_out_uncounted_confirm_title'),
      icon: <ExclamationCircleOutlined />,
      content: t('shared.stock_takings.show.zero_out_uncounted_confirm_content'),
      okText: t('common.action.confirm'),
      cancelText: t('common.action.cancel'),
      onOk: async () => {
        setZeroingOut(true)
        try {
          const response = await stockTakingService.zeroOutUncounted(id)
          if (response.success) {
            const n = response.data?.zeroed_count ?? 0
            if (n === 0) {
              message.info(t('shared.stock_takings.show.zero_out_uncounted_none'))
            } else {
              message.success(t('shared.stock_takings.show.zero_out_uncounted_success', { count: n }))
            }
            loadStockTaking()
          } else {
            message.error(response.message || t('shared.stock_takings.show.zero_out_uncounted_failed'))
          }
        } catch (err) {
          console.error('Zero out uncounted error:', err)
          message.error(t('common.error.unknown_error'))
        } finally {
          setZeroingOut(false)
        }
      }
    })
  }

  // 匯入檔案選擇
  const beforeImportUpload = (file) => {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ]
    if (!validTypes.includes(file.type)) {
      message.error(t('common.import_modal.invalid_file'))
      return false
    }
    setImportFile(file)
    return false
  }

  // 自動 preview：stock_taking 載入且還在 in_progress / approvable 狀態時打一次，
  // section 立即反映目前 inventory 衝突。approved / rejected 終態不必再 preview。
  const fetchPreview = useCallback(async () => {
    if (!stockTaking?.id) return
    if (stockTaking.status === 'approved' || stockTaking.status === 'rejected') return
    setPreviewLoading(true)
    try {
      const response = await stockTakingService.previewApprove(stockTaking.id)
      if (response.success) {
        setConflicts(response.data?.conflicts ?? [])
      } else {
        handleApiError(response, { t })
      }
    } catch (err) {
      handleNetworkError(err, { t })
    } finally {
      setPreviewLoading(false)
    }
  }, [stockTaking?.id, stockTaking?.status, t])

  useEffect(() => {
    fetchPreview()
  }, [fetchPreview])

  // 處理核准：BE 已經把 conflict / partial 兩件事拆乾淨，FE 只負責 race defense。
  // partial（未盤儲位）的提示透過自動 preview 後拿到的 data 在頁面上呈現，這裡不再重打 preview。
  const handleApprove = async () => {
    if (blockIfDirty()) return
    setApproving(true)
    try {
      const response = await stockTakingService.approve(id)
      if (response?.success === true) {
        message.success(t('shared.stock_takings.show.approve_success'))
        loadStockTaking()
        return
      }
      // Race defense：BE 在最後關頭發現可調整量不足（其他人剛剛分配/凍結）
      // → 提示使用者並重新 preview，讓最新衝突重新計算
      if (response?.message === 'adjustment_exceeds_available') {
        message.warning(t('shared.inventory_conflict.race_refreshed_message'))
        await fetchPreview()
        return
      }
      handleApiError(response, { t })
    } catch (err) {
      handleNetworkError(err, { t })
    } finally {
      setApproving(false)
    }
  }

  // 處理拒絕（reject）：標記盤點為已拒絕，不寫入庫存差異。
  // 使用 Modal.confirm 而非 Popconfirm — reject 是不可復原的終態動作（保留稽核但不調整庫存），
  // 與 approve 同樣需要顯眼的雙重確認 + 警告 icon，避免操作員誤點。
  const handleReject = () => {
    if (blockIfDirty()) return

    Modal.confirm({
      title: t('shared.stock_takings.show.reject_confirm_title'),
      icon: <ExclamationCircleOutlined />,
      content: t('shared.stock_takings.show.reject_confirm_content'),
      width: 480,
      okText: t('shared.stock_takings.show.reject_button'),
      okButtonProps: { danger: true },
      cancelText: t('common.action.cancel'),
      onOk: async () => {
        setRejecting(true)
        try {
          const response = await stockTakingService.reject(id)
          // 必須是明確的 success === true。null / {} / 缺欄位的 response 不再被誤判為成功
          // （否則操作員會看到「拒絕成功」toast 但 ST 其實沒動）。
          if (response?.success === true) {
            message.success(t('shared.stock_takings.show.reject_success'))
            loadStockTaking()
          } else {
            handleApiError(response, { t })
          }
        } catch (err) {
          handleNetworkError(err, { t })
        } finally {
          setRejecting(false)
        }
      }
    })
  }

  // 處理刪除
  const handleDelete = () => {
    if (blockIfDirty()) return
    Modal.confirm({
      title: t('shared.stock_takings.show.delete_confirm_title'),
      icon: <ExclamationCircleOutlined />,
      content: t('shared.stock_takings.show.delete_confirm_content'),
      okText: t('common.action.delete'),
      okType: 'danger',
      cancelText: t('common.action.cancel'),
      onOk: async () => {
        setDeleting(true)
        try {
          const response = await stockTakingService.delete(id)
          if (response.success) {
            message.success(t('common.message.delete_success'))
            navigate('/stock_takings')
          } else {
            message.error(response.message || t('common.error.delete_failed'))
          }
        } catch (err) {
          console.error('Delete stock taking error:', err)
          message.error(t('common.error.unknown_error'))
        } finally {
          setDeleting(false)
        }
      }
    })
  }

  // 計算進度
  const getProgress = () => {
    if (!stockTaking) return { completed: 0, total: 0, percent: 0 }
    const total = stockTaking.total_shelves || 0
    const completed = stockTaking.completed_shelves || 0
    const percent = total > 0 ? Math.round((completed / total) * 100) : 0
    return { completed, total, percent }
  }

  const progress = getProgress()

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    )
  }

  if (error) {
    return (
      <Alert
        message={t('common.error.title')}
        description={error}
        type="error"
        showIcon
        style={{ marginBottom: 16 }}
      />
    )
  }

  if (!stockTaking) {
    return (
      <Alert
        message={t('shared.stock_takings.detail.title')}
        description={t('common.error.not_found')}
        type="warning"
        showIcon
      />
    )
  }

  // 4-state lifecycle: not_started / in_progress / approved / rejected
  const isFinalized = stockTaking.status === 'approved' || stockTaking.status === 'rejected'
  const canApprove = stockTaking.approvable === true
  const canReject = stockTaking.rejectable === true
  const canDelete = stockTaking.deletable === true
  const hasConflicts = conflicts.length > 0

  // approve button：有衝突時 disable + tooltip 提示；否則維持原本流程
  const approveButton = (() => {
    const baseButton = (
      <Button
        type="primary"
        icon={<CheckCircleOutlined />}
        loading={approving}
        disabled={hasConflicts || previewLoading}
        onClick={handleApprove}
      >
        {t('shared.stock_takings.show.approve_button')}
      </Button>
    )

    if (hasConflicts) {
      return (
        <Tooltip title={t('shared.inventory_conflict.approve_blocked_tooltip', { count: conflicts.length })}>
          {baseButton}
        </Tooltip>
      )
    }

    return baseButton
  })()

  return (
    <div>
      <ListPageHeader
        items={['stocks', 'stocks.stock_takings', { label: stockTaking.name }]}
        actions={
          <Space>
            {canDelete && (
              <Button
                icon={<DeleteOutlined />}
                danger
                loading={deleting}
                onClick={handleDelete}
              >
                {t('common.action.delete')}
              </Button>
            )}
            {canReject && (
              <Button
                danger
                icon={<StopOutlined />}
                loading={rejecting}
                onClick={handleReject}
              >
                {t('shared.stock_takings.show.reject_button')}
              </Button>
            )}
            {canApprove && approveButton}
          </Space>
        }
      />

      <Card>
        <Descriptions bordered column={2}>
          <Descriptions.Item label={t('models.stock_taking.name')}>
            {stockTaking.name}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.stock_taking.warehouse')}>
            {stockTaking.warehouse_name}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.stock_taking.status')}>
            {(() => {
              // 4-state status colour map
              const colorMap = {
                not_started: 'default',
                in_progress: 'blue',
                approved: 'green',
                rejected: 'red'
              }
              return (
                <Tag color={colorMap[stockTaking.status] || 'default'}>
                  {t(`enums.stock_taking.status.${stockTaking.status}`)}
                </Tag>
              )
            })()}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.stock_taking.mode')}>
            <Tag color={stockTaking.mode === 'static' ? 'purple' : 'cyan'}>
              {t(`enums.stock_taking.mode.${stockTaking.mode}`)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('models.stock_taking.is_blind')}>
            <Tag color={stockTaking.is_blind ? 'orange' : 'default'}>
              {t(`shared.stock_takings.list.blind_types.${stockTaking.is_blind ? 'blind' : 'normal'}`)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('models.stock_taking.progress')}>
            <Space>
              <Progress
                percent={progress.percent}
                size="small"
                style={{ width: 120 }}
              />
              <span>{progress.completed}/{progress.total}</span>
            </Space>
          </Descriptions.Item>
          {stockTaking.finalized_by_name && (
            <Descriptions.Item label={t('models.stock_taking.finalized_by')}>
              {stockTaking.finalized_by_name}
              {stockTaking.status === 'rejected' && (
                <Tag color="error" style={{ marginLeft: 8 }}>
                  {t('shared.stock_takings.list.status.rejected')}
                </Tag>
              )}
              {stockTaking.status === 'approved' && (
                <Tag color="success" style={{ marginLeft: 8 }}>
                  {t('shared.stock_takings.list.status.approved')}
                </Tag>
              )}
            </Descriptions.Item>
          )}
          {stockTaking.finalized_at && (
            <Descriptions.Item label={t('models.stock_taking.finalized_at')}>
              {new Date(stockTaking.finalized_at).toLocaleString('zh-TW')}
            </Descriptions.Item>
          )}
          <Descriptions.Item label={t('common.created_at')}>
            {new Date(stockTaking.created_at).toLocaleString('zh-TW')}
          </Descriptions.Item>
          {stockTaking.note && (
            <Descriptions.Item label={t('models.stock_taking.note')} span={2}>
              {stockTaking.note}
            </Descriptions.Item>
          )}
        </Descriptions>

        {/* 靜態盤點快照狀態提示 */}
        {stockTaking.mode === 'static' && !stockTaking.snapshot_ready && (
          <Alert
            message={
              stockTaking.snapshot_status === 'failed'
                ? t('shared.stock_takings.show.snapshot_failed')
                : t('shared.stock_takings.show.snapshot_preparing')
            }
            description={
              stockTaking.snapshot_status === 'failed'
                ? t('shared.stock_takings.show.snapshot_failed_hint')
                : t('shared.stock_takings.show.snapshot_preparing_hint')
            }
            type={stockTaking.snapshot_status === 'failed' ? 'error' : 'warning'}
            showIcon
            icon={stockTaking.snapshot_status !== 'failed' && <Spin size="small" />}
            style={{ marginTop: 16 }}
            action={
              stockTaking.snapshot_status === 'failed' && (
                <Button size="small" onClick={loadStockTaking}>
                  {t('common.action.retry')}
                </Button>
              )
            }
          />
        )}

        {!isFinalized && !canApprove && stockTaking.total_shelves > 0 && stockTaking.snapshot_ready && (
          <Alert
            message={t('shared.stock_takings.show.cannot_approve_hint')}
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </Card>

      {!isFinalized && (
        <div style={{ marginTop: 16 }}>
          <InventoryConflictSection
            conflicts={conflicts}
            loading={previewLoading}
            onRefresh={fetchPreview}
          />
        </div>
      )}

      {/* 儲位列表 */}
      {stockTaking.total_shelves > 0 && (
        <Card
          title={t('shared.stock_takings.show.shelves_title')}
          style={{ marginTop: 16 }}
        >
          <Table
            columns={[
              {
                title: t('models.shelf.location'),
                dataIndex: 'shelf_name',
                key: 'shelf_name',
              },
              {
                title: t('models.stock_taking_shelf.status'),
                dataIndex: 'status',
                key: 'status',
                width: 120,
                render: (status) => {
                  const colorMap = {
                    pending: 'default',
                    reported: 'processing',
                    checked: 'success'
                  }
                  return (
                    <Tag color={colorMap[status] || 'default'}>
                      {t(`enums.stock_taking_shelf.status.${status}`)}
                    </Tag>
                  )
                }
              },
              {
                title: t('common.action.title'),
                key: 'action',
                width: 100,
                render: (_, record) => (
                  <Button
                    type="link"
                    size="small"
                    onClick={() => navigate(`/stock_takings/${id}/shelves/${record.id}`)}
                  >
                    {t('common.action.view')}
                  </Button>
                )
              }
            ]}
            dataSource={shelves}
            rowKey="id"
            loading={shelvesLoading}
            pagination={{
              current: shelvesPagination.current,
              pageSize: shelvesPagination.pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              total: shelvesPagination.hasMore
                ? (shelvesPagination.current * shelvesPagination.pageSize) + 1
                : shelvesPagination.current * shelvesPagination.pageSize
            }}
            onChange={handleShelvesTableChange}
            size="small"
          />
        </Card>
      )}

      {/* 動態模式：App 寫入提示。finalize 後 App 不該再寫入，提示過時 → 隱藏 */}
      {stockTaking.mode === 'dynamic' && !isFinalized && (
        <Alert
          message={t('shared.stock_takings.show.dynamic_mode_hint')}
          description={t('shared.stock_takings.show.dynamic_mode_hint_desc')}
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      )}

      {/* 動態模式：唯讀的盤點項目列表，提供後台稽核軌跡（盤點人、盤點時間、覆盤次數）。
          finalize 後仍要顯示以供事後查證，對齊 static 分支 finalize 後仍可看的行為。 */}
      {stockTaking.mode === 'dynamic' && (
        <Card
          title={t('shared.stock_takings.show.items_title')}
          style={{ marginTop: 16 }}
        >
          <ItemsSpreadsheet
            key={spreadsheetReloadKey}
            stockTakingId={id}
            isEditable={false}
          />
        </Card>
      )}

      {/* 靜態模式：Spreadsheet 編輯器 */}
      {stockTaking.mode === 'static' && stockTaking.snapshot_ready && (
        <Card
          title={t('shared.stock_takings.show.items_title')}
          style={{ marginTop: 16 }}
          extra={
            !isFinalized && (
              <Space>
                <Button
                  icon={<DownloadOutlined />}
                  loading={exporting}
                  onClick={handleExportItems}
                >
                  {t('shared.stock_takings.show.export_items')}
                </Button>
                <Button
                  icon={<UploadOutlined />}
                  onClick={() => setImportModalOpen(true)}
                >
                  {t('shared.stock_takings.show.import_items')}
                </Button>
                <Button
                  loading={zeroingOut}
                  onClick={handleZeroOutUncounted}
                >
                  {t('shared.stock_takings.show.zero_out_uncounted')}
                </Button>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  loading={confirmingAll}
                  onClick={handleConfirmAllShelves}
                >
                  {t('shared.stock_takings.show.confirm_all_shelves')}
                </Button>
              </Space>
            )
          }
        >
          <ItemsSpreadsheet
            key={spreadsheetReloadKey}
            stockTakingId={id}
            isEditable={!isFinalized}
            onDirtyChange={setSpreadsheetDirtyCount}
          />
        </Card>
      )}

      {/* 匯入 Modal */}
      <Modal
        title={t('shared.stock_takings.show.import_modal_title')}
        open={importModalOpen}
        onCancel={() => {
          setImportModalOpen(false)
          setImportFile(null)
        }}
        onOk={handleImportItems}
        confirmLoading={importing}
        okText={t('common.action.import')}
        cancelText={t('common.action.cancel')}
        destroyOnClose
      >
        <Alert
          message={t('common.import_modal.description')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Dragger
          accept=".xlsx,.xls"
          beforeUpload={beforeImportUpload}
          onRemove={() => setImportFile(null)}
          fileList={importFile ? [importFile] : []}
          maxCount={1}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            {t('common.import_modal.drag_or_click')}
          </p>
          <p className="ant-upload-hint">
            {t('common.import_modal.file_format_hint')}
          </p>
        </Dragger>
      </Modal>
    </div>
  )
}
